export default {
  methods: {
    // 兼容后端和微信侧不同字段名，统一取订阅模板 ID。
    getSubscribeTemplateId(item = {}) {
      return item.priTmplId || item.primTplId || item.template_id || item.templateId || '';
    },

    // 只返回本次需要唤起微信订阅的模板：过滤无 ID、模板自身已成功、成功 ID 列表已包含的模板。
    getPendingSubscribeTemplates() {
      const successTemplateIdSet = new Set((this.success_template_ids || []).map(String));

      return (this.subscribe_template || []).filter((item) => {
        const templateId = this.getSubscribeTemplateId(item);
        if (!templateId) return false;
        if (Number(item.has_success_subscribe) === 1) return false;
        return !successTemplateIdSet.has(String(templateId));
      });
    },

    // 判断本次微信回调里是否至少有一个模板订阅成功。
    isWechatSubscribeAccepted(detail = {}) {
      const subscribeResults = this.getWechatSubscribeResultMap(detail);
      return Object.keys(subscribeResults).some((templateId) => {
        return this.isWechatSubscribeResultAccepted(subscribeResults[templateId]);
      });
    },

    // 微信 subscribeDetails 可能是字符串，也可能内部模板结果是 JSON 字符串，这里统一转成对象。
    normalizeWechatSubscribeDetails(subscribeDetails = {}) {
      if (typeof subscribeDetails === 'string') {
        try {
          subscribeDetails = JSON.parse(subscribeDetails);
        } catch (error) {
          subscribeDetails = {};
        }
      }

      return Object.keys(subscribeDetails || {}).reduce((result, templateId) => {
        let detail = subscribeDetails[templateId];
        if (typeof detail === 'string' && detail.indexOf('{') === 0) {
          try {
            detail = JSON.parse(detail);
          } catch (error) {}
        }
        result[templateId] = detail;
        return result;
      }, {});
    },

    // 微信可能把模板结果放在 detail 根节点或 subscribeDetails 里，统一合并成 { 模板ID: 结果 }。
    getWechatSubscribeResultMap(detail = {}) {
      const subscribeDetails = this.normalizeWechatSubscribeDetails(detail.subscribeDetails);
      const detailResults = Object.keys(detail || {}).reduce((result, templateId) => {
        if (['errMsg', 'errCode', 'errcode', 'err_code', 'subscribeDetails'].includes(templateId)) {
          return result;
        }
        result[templateId] = detail[templateId];
        return result;
      }, {});

      return {
        ...detailResults,
        ...subscribeDetails,
      };
    },

    // 微信官方成功状态兼容字符串 accept 和数字 1。
    isWechatSubscribeResultAccepted(result) {
      const status = this.getWechatSubscribeResultStatus(result);
      return status === 1 || status === '1' || status === 'accept';
    },

    // 模板结果可能直接是状态值，也可能是 { status } 对象，统一取出状态。
    getWechatSubscribeResultStatus(result) {
      if (result && typeof result === 'object' && result.status !== undefined) {
        return result.status;
      }
      return result;
    },

    // 转换为后端业务状态：1 成功、2 用户取消、3 失败；无明确结果时使用默认状态。
    getWechatTemplateSubscribeStatus(templateId, detail = {}, defaultStatus = 2) {
      const subscribeResults = this.getWechatSubscribeResultMap(detail);
      const result = this.getWechatSubscribeResultStatus(subscribeResults[templateId]);

      if (this.isWechatSubscribeResultAccepted(result)) return 1;
      if (result === 3 || result === '3' || result === 'fail') return 3;
      return defaultStatus;
    },

    // 保留微信官方返回的原始模板订阅状态，用于上报 subscribe_status。
    getWechatTemplateOriginSubscribeStatus(templateId, detail = {}) {
      const subscribeResults = this.getWechatSubscribeResultMap(detail);
      const result = this.getWechatSubscribeResultStatus(subscribeResults[templateId]);
      return result === undefined || result === null ? '' : result;
    },

    // 从模板级结果里提取失败原因，兼容不同命名。
    getWechatSubscribeResultReason(result) {
      if (!result || typeof result !== 'object') return '';
      return result.fail_reason || result.reason || result.errMsg || '';
    },

    // 从模板级结果里提取失败码，兼容微信不同 errCode 命名。
    getWechatSubscribeResultFailCode(result) {
      if (!result || typeof result !== 'object') return '';
      return result.fail_code || result.errCode || result.errcode || result.err_code || '';
    },

    // 生成保存接口需要的模板列表，仅上报本次唤起的未成功模板。
    getWechatSubscribeTemplates(detail = {}, defaultStatus = 2) {
      return this.getPendingSubscribeTemplates()
        .map((item) => {
          const templateId = this.getSubscribeTemplateId(item);
          if (!templateId) return null;
          const status = this.getWechatTemplateSubscribeStatus(templateId, detail, defaultStatus);
          const subscribeStatus = this.getWechatTemplateOriginSubscribeStatus(templateId, detail);
          return {
            priTmplId: templateId,
            title: item.title || '',
            status,
            subscribe_status: subscribeStatus,
            fail_code: this.getWechatSubscribeFailCode(detail, status, templateId),
            fail_reason: this.getWechatSubscribeFailReason(detail, status, templateId),
          };
        })
        .filter(Boolean);
    },

    // 成功不传失败原因；取消默认补“用户取消”；失败优先取模板级原因，再取整体 errMsg。
    getWechatSubscribeFailReason(detail = {}, status, templateId) {
      if (status === 1) return '';
      const templateResult = this.getWechatSubscribeResultMap(detail)[templateId];
      if (status === 2) return this.getWechatSubscribeResultReason(templateResult) || '用户取消';
      return this.getWechatSubscribeResultReason(templateResult) || detail.errMsg || '';
    },

    // 只有失败状态才上报失败码，优先取模板级失败码，再取整体回调失败码。
    getWechatSubscribeFailCode(detail = {}, status, templateId) {
      if (status !== 3) return '';
      const templateResult = this.getWechatSubscribeResultMap(detail)[templateId];
      return (
        this.getWechatSubscribeResultFailCode(templateResult) ||
        detail.errCode ||
        detail.errcode ||
        detail.err_code ||
        ''
      );
    },

    // 统一保存接口 payload，保留微信原始回调方便后端排查。
    getWechatSubscribePayload(detail = {}, defaultStatus = 2) {
      return {
        templates: this.getWechatSubscribeTemplates(detail, defaultStatus),
        wx_result: detail,
      };
    },

    // 订阅成功后本地同步模板状态，避免不刷新页面时重复展示已成功订阅的模板。
    updateSubscribeStateByWechatResult(detail = {}) {
      const subscribeResults = this.getWechatSubscribeResultMap(detail);
      const acceptedTemplateIds = Object.keys(subscribeResults).filter((templateId) => {
        return this.isWechatSubscribeResultAccepted(subscribeResults[templateId]);
      });

      if (!acceptedTemplateIds.length) return;

      const acceptedTemplateIdSet = new Set(acceptedTemplateIds.map(String));
      this.success_template_ids = Array.from(
        new Set([
          ...(this.success_template_ids || []).map(String),
          ...acceptedTemplateIds.map(String),
        ])
      );
      this.subscribe_template = (this.subscribe_template || []).map((item) => {
        const templateId = this.getSubscribeTemplateId(item);
        if (!acceptedTemplateIdSet.has(String(templateId))) return item;
        return {
          ...item,
          has_success_subscribe: 1,
        };
      });
      this.has_success_subscribe = this.getPendingSubscribeTemplates().length ? 0 : 1;
    },
  },
};
